import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Input,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import {
  ButtonsModule,
  CardModule,
  CheckboxModule,
  IconModule,
  LoadingService,
} from 'axis-components';
import { MediaModule } from 'axis-components/media';
import { TabsModule } from 'axis-components/tab';
import { PdfService } from '../../../_services/pdf/pdf.service';
import { RequestService } from '../../../_services/request/request.service';
import { SignatureService } from '../../../_services/uim-ui/signature.service';
import { UserService } from '../../../_services/user.service';
import { AnalyticsService } from '../../../_shared/analytics/analytics.service';
import { SignatureComponent } from '../../../_shared/signature/signature.component';
import {
  FormType,
  PDFGenerationStatus,
  RequestProgressStep,
  UserRole,
} from '../../../config/constants';
import { UimuiBaseRulesService } from '../../../state-disclosures/uimui/services/uimui-base-rules.service';
import { BrowserNavigationService } from '../../../_services/browser-navigation.service';
import { finalize } from 'rxjs';
import { UimuiBaseStateComponent } from '../../../state-disclosures/uimui/uimui-base-state.component';

@Component({
  selector: 'app-uimui-signature',
  standalone: true,
  imports: [
    TabsModule,
    CardModule,
    CommonModule,
    ButtonsModule,
    CheckboxModule,
    MediaModule,
    IconModule,
    ReactiveFormsModule,
    SignatureComponent,
  ],
  templateUrl: './uimui-signature.component.html',
  styleUrl: './uimui-signature.component.scss',
})
export class UimuiSignatureComponent implements OnInit, OnDestroy {
  // Events emitted to playground component for workflow navigation
  @Output() continueClickedPlayground = new EventEmitter<void>();
  @Output() backClickedPlayground = new EventEmitter<void>();
  @Input() ctaData: any = {};

  public unSignature: FormGroup;
  signatureData: any;
  signaturePad = viewChild<SignatureComponent>('signaturePad');
  dateStr: string;
  showSignatureError: boolean = false;
  role;
  isOnlyUMUIM: boolean = false;
  accountDetails: any = null;
  constructor(
    private readonly signatureService: SignatureService,
    public userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private baseRulesService: UimuiBaseRulesService,
    private fb: FormBuilder,
    private pdfService: PdfService,
    private requestService: RequestService,
    private analyticsService: AnalyticsService,
    private browserNavigationService: BrowserNavigationService,
    private loadingService: LoadingService,
  ) {}

  ngOnInit(): void {
    this.browserNavigationService.startListening({
      onBackNavigation: () => this.onBrowserback(),
    });

    this.role = this.userService.role;
    this.getAccountDetails();
    this.requestService.checkIfFormIsCompleted();
    const nameAndTitle = this.accountDetails
      ? ` ${this.accountDetails.insuredName || ''} ${this.accountDetails.title || ''}`.trim()
      : '';
    if (
      this.requestService.requestFormTypeList.length === 1 &&
      this.requestService.requestFormTypeList[0] === FormType.UM_UIM
    ) {
      this.isOnlyUMUIM = true;
    } else {
      this.isOnlyUMUIM = false;
    }

    if (this.role === UserRole.BROKER) {
      this.unSignature = this.fb.group({
        unSignCheck: ['', Validators.required],
        unInsuredName: [{ value: '', disabled: true }],
      });
    } else if (this.role === UserRole.INSURED) {
      this.unSignature = this.fb.group({
        unSignCheck: ['', Validators.required],
        unInsuredName: [
          { value: nameAndTitle, disabled: false },
          (Validators.required, Validators.pattern(/^[^\d]+$/)),
        ],
      });
    }

    this.signatureService.getSignatureData().subscribe((data) => {
      this.signatureData = data;
    });
    this.goTop();
  }

  ngOnDestroy(): void {
    this.browserNavigationService.stopListening();
  }

  onBrowserback(): void {
    this.updateonback();
  }

  updateonback(): void {
    this.baseRulesService.pdfDataDump = {
      ...this.baseRulesService.pdfSummaryDataDump,
    };
    this.baseRulesService.isContinueButtonClicked = false;
  }
  onSignatureChange(): void {
    this.showSignatureError = false;
  }
  getAccountDetails(): void {
    if (
      this.requestService.request &&
      this.requestService.request.requestDetails &&
      this.requestService.request.requestDetails.accountDetail
    ) {
      this.accountDetails =
        this.requestService.request.requestDetails.accountDetail;
    }
  }
  goTop(): void {
    // reset the page when changing route
    window.scrollTo(0, 0);
  }
  onBack(): void {
    if (this.baseRulesService.isPlayground) {
      // In playground mode, emit event instead of navigating
      this.backClickedPlayground.emit();
      return;
    }

    this.updateonback();
    this.router.navigate(['/insured/uimui-outstanding-state'], {
      queryParamsHandling: 'preserve',
    });
  }

  onContinue(): void {
    if (this.role == UserRole.BROKER) {
      // Get current origin for dynamic URL construction
      const baseUrl = window.location.origin;
      const targetRoute = '/insured/pip-consent';

      // Setup CTA data for PIP start tracking
      this.ctaData.ctaName = 'DigiSign:pip:start:continue';
      this.ctaData.ctaUrl = `${baseUrl}${targetRoute}`;
      this.analyticsService.setAdobeDataLayerCTAEvent(this.ctaData);
      this.router.navigate([targetRoute], {
        queryParamsHandling: 'preserve',
      });
      return;
    }
    let isValid = false;
    const signatureData = this.signaturePad().signatureData;
    if (!signatureData?.data || signatureData.data.trim() === '') {
      this.showSignatureError = true;
      isValid = true;
    } else {
      this.showSignatureError = false;
    }
    if (this.unSignature?.invalid) {
      Object.keys(this.unSignature?.controls).forEach((key) => {
        this.unSignature.get(key)?.markAsTouched();
      });
      isValid = true;
    }
    if (isValid) {
      window.scrollTo(0, 0);
      return;
    }
    const reqEffectiveDate =
      this.requestService.request?.requestDetails?.policyDetail?.[0]
        ?.effectiveDate;
    // Format the effective date from MM-DD-YYYY to MM/DD/YYYY
    const formattedEffectiveDate = reqEffectiveDate
      ? reqEffectiveDate.replace(/-/g, '/')
      : '';
    const signatureObj = {
      nameTitle: this.unSignature.value['unInsuredName'],
      method: this.signaturePad().signatureData.method,
      data: this.signaturePad().signatureData.data,
      effectiveDate: formattedEffectiveDate,
      dateSigned: this.formatDate(new Date().toISOString()),
    };
    this.pdfService.signatureObject = signatureObj;

    if (this.baseRulesService.isPlayground) {
      // In playground mode, emit event instead of navigating
      this.continueClickedPlayground.emit();
      return;
    }

    /*
    Object.keys(this.baseRulesService.pdfDataDump).forEach((state) => {
      this.baseRulesService.pdfDataDump[state] = {
        ...this.baseRulesService.pdfDataDump[state],
        ...signatureObj,
      };
    });
    */

    const submitWithCompletionGuard = (wasAlreadyCompleted: boolean) => {
      const isUimuiCompleted = this.requestService.isFormTypeAlreadyCompleted(
        FormType.UM_UIM,
      );

      // If UM/UIM is already completed, do not continue with consent routing.
      if (isUimuiCompleted) {
        return;
      }
      UimuiBaseStateComponent.resetLoadingState();
      this.loadingService.start('Loading');
      // Wait for step update to complete before navigating
      this.requestService
        .updateRequestProgress(
          FormType.UM_UIM,
          RequestProgressStep.ELECTRONIC_SIGNATURE,
          this.baseRulesService.dataDump,
        )
        .then(() => {
          this.analyticsService.logUIUMSelectionOfCoverages();

          // Get current origin for dynamic URL construction
          const baseUrl = window.location.origin;
          let targetRoute: string;

          if (this.isOnlyUMUIM) {
            targetRoute = '/thank-you';
          } else {
            targetRoute = '/insured/uimui-thankyou';
          }

          // Only emit completion when submit newly completes the UM/UIM form.
          if (!wasAlreadyCompleted) {
            this.analyticsService.emitFormCompleteOnce(
              FormType.UM_UIM,
              'DigiSign:um_uim:thankYou',
              targetRoute,
            );
          }

          // Setup CTA data for completion tracking
          this.ctaData.ctaName = 'DigiSign:um_uim:signature:continue';
          this.ctaData.ctaUrl = `${baseUrl}${targetRoute}`;
          this.analyticsService.setAdobeDataLayerCTAEvent(this.ctaData);
          this.pdfService.uiumPdfGenerationStatus =
            PDFGenerationStatus.RequestSent;
          this.pdfService
            .generateUMUIMSendEmailPdf()
            .pipe(
              finalize(() => {
                this.loadingService.stop();
                // Navigate to thank you page after step update is complete
                if (this.isOnlyUMUIM) {
                  this.router.navigate(['/thank-you'], {
                    queryParamsHandling: 'preserve',
                  });
                } else {
                  this.router.navigate(['/insured/uimui-thankyou'], {
                    queryParamsHandling: 'preserve',
                  });
                }
              }),
            )
            .subscribe(
              (res) => {
                this.pdfService.uiumPdfGenerationStatus =
                  PDFGenerationStatus.ResponseReceived;
              },
              (err) => {
                console.error('Error sending Email with PDF:', err);
                this.pdfService.uiumPdfGenerationStatus =
                  PDFGenerationStatus.Error;
              },
            );
        })
        .catch((err) => {
          console.error('Error updating UIMUI step:', err);
          // Navigate anyway to avoid blocking user
          if (this.isOnlyUMUIM) {
            this.router.navigate(['thank-you'], {
              queryParamsHandling: 'preserve',
            });
          } else {
            this.router.navigate(['/insured/uimui-thankyou'], {
              queryParamsHandling: 'preserve',
            });
          }
        });
    };

    // Refresh from DB before submit to guard duplicate completes across sessions/browsers.
    this.requestService.refreshRequestData(false).subscribe({
      next: () => {
        submitWithCompletionGuard(
          this.requestService.isFormTypeAlreadyCompleted(FormType.UM_UIM),
        );
      },
      error: () => {
        submitWithCompletionGuard(
          this.requestService.isFormTypeAlreadyCompleted(FormType.UM_UIM),
        );
      },
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';

    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);

    const formattedMonth = String(month).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');

    return `${formattedMonth}/${formattedDay}/${year}`;
  }
}
